import { Option } from 'models/Global';
import { includesIgnoreCase, isSingleElementArray } from 'utils';
import isEqual from 'lodash/isEqual';

export const isSortableCol = (
  key: string,
  sortingValues: Record<string, string>,
) => sortingValues && Object.keys(sortingValues).includes(key);

export const hasSettings = (
  key: string,
  onSettings: Record<string, () => void>,
) => onSettings && Object.keys(onSettings).includes(key);

export const mergeColumnOrder = (
  defaultColumns: string[],
  persistedOrder: string[],
) => {
  const known = persistedOrder.filter((col) => defaultColumns.includes(col));

  return defaultColumns.reduce(
    (order, col, index) =>
      known.includes(col)
        ? order
        : [...order.slice(0, index), col, ...order.slice(index)],
    known,
  );
};

export const applyReorderedVisibleColumns = (
  fullOrder: string[],
  newVisibleOrder: string[],
  hiddenColumns: string[],
) => {
  const order = [...newVisibleOrder];

  return fullOrder.map((col) =>
    hiddenColumns.includes(col) ? col : order.shift(),
  );
};

export const filterColumnsBySearch = (options: Option[], search: string) => {
  const normalized = search.trim();

  if (!normalized) return options;

  return options.filter(({ label }) => includesIgnoreCase(label, normalized));
};

export const getIsShortenedCounterForm = (totalCount: number, maxCount = 10) =>
  totalCount > maxCount;

export const computeHideAllColumns = (
  allColumns: string[],
  lockedColumn?: string,
) => allColumns.filter((col) => col !== (lockedColumn ?? allColumns[0]));

export const getIsLastVisibleColumn = (
  col: string,
  hiddenColumns: string[],
  allColumns: string[],
) =>
  !hiddenColumns.includes(col) &&
  isSingleElementArray(allColumns.filter((c) => !hiddenColumns.includes(c)));

export const toggleColumnVisibility = (
  hiddenColumns: string[],
  col: string,
  allColumns: string[],
  lockedColumn?: string,
): string[] => {
  const isHidden = hiddenColumns.includes(col);

  if (
    !isHidden &&
    (col === lockedColumn ||
      getIsLastVisibleColumn(col, hiddenColumns, allColumns))
  )
    return hiddenColumns;

  return isHidden
    ? hiddenColumns.filter((c) => c !== col)
    : [...hiddenColumns, col];
};

export const getIsDefaultColumns = (
  columns: string[],
  hiddenColumns: string[],
  defaultHiddenColumns: string[],
  columnOrder: string[],
) =>
  isEqual(
    columns.filter((col) => hiddenColumns.includes(col)),
    columns.filter((col) => defaultHiddenColumns.includes(col)),
  ) && isEqual(mergeColumnOrder(columns, columnOrder), columns);

export const getColumnSettings = (
  hideKey: string,
  orderKey: string,
  hiddenColumns: string[],
  columnOrder: string[],
) => ({
  ...(hideKey && { [hideKey]: hiddenColumns }),
  ...(orderKey && { [orderKey]: columnOrder }),
});

export const getTableHiddenColumns = (
  allColumns: string[],
  hiddenColumns: string[],
) => hiddenColumns.filter((col) => allColumns.includes(col));
